import React from "react";

import { Error, Info } from "@mui/icons-material";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useParams } from "react-router-dom";

import { UrlParamTypes } from "../../../../types/org-display";
import { useTrainingMigrationContext } from "./context/training-migration-provider";

export const CompareGroupsForm: React.FC = () => {
  const theme = useTheme();
  const { roleId } = useParams<UrlParamTypes>();
  const { compareGroupsFormState, setCompareGroupsFormState } =
    useTrainingMigrationContext();

  return (
    <Grid container paddingTop={2}>
      {/* Info Panel */}
      <Grid item xs={12} container direction="column">
        <Box
          bgcolor={theme.palette.info.light}
          borderRadius="5px"
          border={1}
          borderColor={theme.palette.info.main}
          padding={2}
          data-testid="compare-groups-info-panel"
        >
          <Grid container alignItems="flex-start" spacing={2}>
            <Grid item>
              <Info color="info" fontSize="large" />
            </Grid>
            <Grid item xs={10}>
              <Typography variant="body1">
                <strong>Start Compare SC and Training Groups Script</strong>
              </Typography>
              <Typography variant="body2" component="div">
                <ul>
                  <li>
                    Fetches lists of sites/groups from SC and Training services
                  </li>
                  <li>Finds discrepancies between the two sources</li>
                  <li>Writes findings to the hippo-hangfire logs</li>
                  <li>Optionally creates missing groups in Training</li>
                </ul>
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Grid>

      {/* Organization ID */}
      <Grid container direction="row" alignItems="center" paddingTop={2}>
        <Grid item xs={5}>
          <Typography>Organisation ID</Typography>
        </Grid>
        <Grid item xs={7}>
          <TextField
            name="Organisation ID"
            inputProps={{
              "data-testid": "input-organisation-id",
            }}
            fullWidth
            disabled
            margin="normal"
            variant="outlined"
            value={roleId}
          />
        </Grid>
      </Grid>

      {/* Dry Run Checkbox */}
      <Grid container direction="row" alignItems="center" paddingTop={1}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={compareGroupsFormState.dryRun}
                onChange={(e) =>
                  setCompareGroupsFormState({
                    ...compareGroupsFormState,
                    dryRun: e.target.checked,
                  })
                }
                data-testid="input-dry-run"
              />
            }
            label={
              <Box>
                <Typography variant="body1">Dry Run</Typography>
                <Typography variant="body2" color="textSecondary">
                  When enabled, only logs actions without making changes
                </Typography>
              </Box>
            }
          />
        </Grid>
      </Grid>

      {/* Create Missing Groups Checkbox */}
      <Grid container direction="row" alignItems="center" paddingTop={1}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={compareGroupsFormState.createMissingGroups}
                onChange={(e) =>
                  setCompareGroupsFormState({
                    ...compareGroupsFormState,
                    createMissingGroups: e.target.checked,
                  })
                }
                data-testid="input-create-missing-groups"
              />
            }
            label={
              <Box>
                <Typography variant="body1">Create Missing Groups</Typography>
                <Typography variant="body2" color="textSecondary">
                  Automatically create groups that exist in SC but not in
                  Training
                </Typography>
              </Box>
            }
          />
        </Grid>
      </Grid>

      {/* Warning for non-dry-run mode */}
      {!compareGroupsFormState.dryRun && (
        <Grid item xs={12} container direction="column" paddingTop={2}>
          <Box
            bgcolor={theme.palette.warning.main}
            borderRadius="5px"
            border={1}
            borderColor={theme.palette.warning.main}
            padding={2}
            data-testid="warning-panel"
          >
            <Grid container alignItems="center" spacing={1}>
              <Grid item>
                <Error fontSize="medium" />
              </Grid>
              <Grid item xs={10}>
                <Typography variant="body2">
                  <strong>Warning:</strong> Dry run is disabled. This script
                  will make actual changes to the Training system.
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};
